import { Init } from "@pdfking/reactivity/preact"
import { FullMethodDispatcher, Items, KeyDrop, RecordMap } from "@pdfking/tfunc"

import type { AnyNode, ArrayForm, AtomForm, ChoiceForm, EnumForm, ObjectForm } from "#public"
import type { ValidationResult } from "#public"

import { ConfirmAllValid } from "../../internal/ConfirmAllValid"
import { FormType } from "../types/FormType"

// The wrapper owns `validate`/`value`/`error` — drop them from the proto before
// spreading the rest (enum, input, format, choices, …) into the live `data`.
const stripAtom = (node: AtomForm<unknown> | ChoiceForm<unknown> | ChoiceForm<unknown[]>) =>
  KeyDrop(node, ["validate", "value", "error"])

// Containers additionally own `results` (computed over live children) and derive
// `input` from them, so drop the proto's static results/input too.
const stripContainer = (
  node: ObjectForm<Record<string, unknown>> | ArrayForm<unknown> | EnumForm<{ enum: string }>
) => KeyDrop(node, ["validate", "value", "error", "results", "input"])

type Reactive = ValidationResult<unknown> & {
  enum: FormType
  input: unknown
  // an enum's live variant tag
  selected?: string
  // containers expose live reactive children (read side); the setter also
  // accepts a validated node's stateless `results` and distributes them down
  // (write side), so the type carries both shapes.
  results?: Record<string, Reactive> | Reactive[] | Record<string, AnyNode> | AnyNode[]
  result: ValidationResult<unknown>
  validate: (input: unknown) => unknown
}

// One reactive tree shares a link to its root and the root proto, so a
// `validate` on any node can revalidate the whole form (running every
// validator) from the top.
type Link = { root: Reactive; proto: AnyNode }

const isContainer = (
  node: AnyNode
): node is ObjectForm<Record<string, unknown>> | ArrayForm<unknown> =>
  node.enum === FormType.OBJECT || node.enum === FormType.ARRAY

// Write a validated stateless node into a reactive one: either the container
// `results` (setter recurses into children; the container's own result and raw
// `input` are computed from them) or the atom's `input` + stored `result`.
// Validators only ever fail atoms, so a container never carries its own error.
// A container never stores the raw record it was validated with: that record can
// carry keys this form does not own (two forms seeded from one payload), and it
// would go stale on the next child edit.
// An enum takes its tag first (the `results` setter only fills that variant),
// then stores the result like an atom.
const assign = (node: Reactive, next: AnyNode): void => {
  if (next.enum === FormType.ENUM) {
    node.selected = next.selected
    node.results = next.results
    node.result = next
  } else if (isContainer(next)) node.results = next.results
  else {
    node.input = next.input
    node.result = next
  }
}

// `validate` never validates locally only: it assigns the new input in place
// (so the tree reflects it, incl. array length), then revalidates from the root
// with the reconstructed input and assigns the result back down.
const validateFn = (proto: AnyNode, link: Link) =>
  function (this: Reactive, input: unknown) {
    assign(this, proto.validate(input))
    if (this !== link.root) assign(link.root, link.proto.validate(link.root.input))
    return this
  }

// `value`/`error` always read from `result`.
const value = function (this: { result: ValidationResult<unknown> }) {
  return this.result.value
}
const error = function (this: { result: ValidationResult<unknown> }) {
  return this.result.error
}

// Atom — `result` is a stored signal; `value`/`error` read off it.
const wrapAtom = (
  proto: AtomForm<unknown> | ChoiceForm<unknown> | ChoiceForm<unknown[]>,
  link: Link
): Reactive =>
  Init({
    data: { ...stripAtom(proto), result: proto as ValidationResult<unknown> },
    computed: { value, error },
    methods: { validate: validateFn(proto, link) },
  }) as Reactive

// Object — `results` is a computed over the live `children`; its setter assigns
// each validated child in place. `result` aggregates structurally.
const wrapObject = (proto: ObjectForm<Record<string, unknown>>, link: Link): Reactive =>
  Init({
    data: {
      ...stripContainer(proto),
      children: RecordMap(child => wrap(child, link), proto.results),
    },
    computed: {
      value,
      error,
      result(this: { children: Record<string, Reactive> }) {
        return ConfirmAllValid(this.children)
      },
      // Rebuilt from the live children, so it holds exactly the keys this form
      // owns and follows every edit.
      input(this: { children: Record<string, Reactive> }) {
        return RecordMap(child => child.input, this.children)
      },
      results: {
        get(this: { children: Record<string, Reactive> }) {
          return this.children
        },
        set(this: { children: Record<string, Reactive> }, next: Record<string, AnyNode>) {
          for (const [key, child] of Items(this.children)) assign(child, next[key])
        },
      },
    },
    methods: { validate: validateFn(proto, link) },
  }) as Reactive

// Array — same as object, but the `results` setter grows/shrinks `children` to
// the validated list length. Children start empty; the first validate fills them
// (so nothing from the proto's initial results is captured).
const wrapArray = (proto: ArrayForm<unknown>, link: Link): Reactive =>
  Init({
    data: {
      ...stripContainer(proto),
      children: [] as Reactive[],
    },
    computed: {
      value,
      error,
      result(this: { children: Reactive[] }) {
        return ConfirmAllValid(this.children)
      },
      // Same as the object: the live rows are the raw input.
      input(this: { children: Reactive[] }) {
        return this.children.map(child => child.input)
      },
      results: {
        get(this: { children: Reactive[] }) {
          return this.children
        },
        // grow/shrink to the validated list: reuse existing slots, wrap fresh
        // ones — either way assign the validated child so containers fill in
        // (a freshly-wrapped container starts empty).
        set(this: { children: Reactive[] }, next: AnyNode[]) {
          const current = this.children
          this.children = next.map((child, i) => {
            const node = current[i] ?? wrap(child, link)
            assign(node, child)
            return node
          })
        },
      },
    },
    methods: { validate: validateFn(proto, link) },
  }) as Reactive

// Enum — one live object child per variant; `selected` and `result` are stored
// signals (assigned on every revalidate), `input` is the selected variant's
// input tagged with `enum`. The `results` setter fills only the selected
// variant, so the others keep their edits across switches.
const wrapEnum = (proto: EnumForm<{ enum: string }>, link: Link): Reactive =>
  Init({
    data: {
      ...stripContainer(proto),
      result: proto as ValidationResult<unknown>,
      children: RecordMap(child => wrap(child, link), proto.results),
    },
    computed: {
      value,
      error,
      input(this: { selected?: string; children: Record<string, Reactive> }) {
        const { selected, children } = this
        return selected ? { enum: selected, ...(children[selected].input as object) } : undefined
      },
      results: {
        get(this: { children: Record<string, Reactive> }) {
          return this.children
        },
        set(
          this: { selected?: string; children: Record<string, Reactive> },
          next: Record<string, AnyNode>
        ) {
          if (this.selected) assign(this.children[this.selected], next[this.selected])
        },
      },
    },
    methods: { validate: validateFn(proto, link) },
  }) as Reactive

// Internal builder: dispatch on form kind, threading the shared link.
const wrap = FullMethodDispatcher<[AnyNode, Link], Reactive>({
  [FormType.ATOM]: wrapAtom,
  [FormType.CHOICE]: wrapAtom,
  [FormType.OBJECT]: wrapObject,
  [FormType.ARRAY]: wrapArray,
  [FormType.ENUM]: wrapEnum,
})

// Build a reactive twin of a stateless form. Same shape and type as the proto:
// signal-backed fields. `validate` on the root or any nested field revalidates
// the whole form from the root, so every validator runs on every edit.
export const CreateReactiveForm = <N extends AnyNode>(proto: N): N => {
  const link = { proto } as Link
  link.root = wrap(proto, link)
  return link.root as unknown as N
}
